'use client';

import { useEffect, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { scrollToTop } from '@/lib/scroll-to-top';
import { VA_EVENTS } from '@/lib/analytics/event-catalog';
import { vaTrack } from '@/lib/analytics/vercel-events';
import { useReducedMotion } from '@/lib/use-reduced-motion';
import { useIsMobile } from '@/hooks/use-mobile';

export default function ScrollToTopButton() {
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const previousScrollBehavior = document.documentElement.style.scrollBehavior;

    if (!prefersReducedMotion) {
      document.documentElement.style.scrollBehavior = 'smooth';
    }

    return () => {
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
    };
  }, [prefersReducedMotion]);

  useEffect(() => {
    const updateVisibility = () => {
      setIsVisible(window.scrollY > window.innerHeight);
    };

    updateVisibility();
    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);

    return () => {
      window.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
    };
  }, []);

  const handleScrollToTop = () => {
    vaTrack(VA_EVENTS.SCROLL_TO_TOP);
    scrollToTop(prefersReducedMotion ? 'auto' : 'smooth');
  };

  return (
    <motion.div
      aria-hidden={!isVisible}
      initial={false}
      animate={{
        opacity: isVisible ? 1 : 0,
        scale: isVisible ? 1 : isMobile ? 0.98 : 0.92,
        y: isVisible ? 0 : isMobile ? 3 : 8,
      }}
      transition={{ duration: isMobile ? 0.16 : 0.22, ease: 'easeOut' }}
      className={cn(
        'fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-50',
        isVisible ? 'pointer-events-auto' : 'pointer-events-none',
      )}
    >
      <Button
        type="button"
        size="icon-lg"
        aria-label="Scroll to top"
        onClick={handleScrollToTop}
        className="size-11 sm:size-12 cursor-pointer rounded-full bg-[#948B7F] hover:bg-[#BFB7A1] text-white hover:text-white shadow-[0_18px_48px_rgba(15,23,42,0.22)] transition-all duration-300 ease-in-out"
      >
        <ArrowUp className="size-5 xl:size-6 shrink-0" />
      </Button>
    </motion.div>
  );
}
